import { CmdInterpreter } from 'BMapsCmds';
import { App } from 'BMapsSrc/BMapsApp';
import { EventBroker } from 'BMapsSrc/eventbroker';
import { wordsToCommands } from 'BMapsUtil/speech_utils';
import { getChainForIndex, getIndexForChain } from 'BMapsUtil/js_utils';

const COMPOUND_LABEL_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

/**
 * @typedef {{ resSpec, cmpdIndex, selectString }} EntityRef
 * cmpdIndex is a string label like "A", "B", ..., "Z", "AA", ...
 */

/**
 * @description Takes a string input, converts it to commands using the commands API,
 * applies the resulting command representations, and publishes human friendly descriptions.
 * @param {string} input
 * @returns {Promise<void>}
 */
export async function applyWordsAsCommands(input) {
    if (!input || input.length === 0) {
        return;
    }

    const context = {};
    const focusCmpd = App.Workspace.getActiveCompound();
    if (focusCmpd) {
        context.focusCompound = { cmpdIndex: getAliasForCompound(focusCmpd) };
    }
    const commandArr = await wordsToCommands(input, context);
    EventBroker.publish('commandInput', { inputWords: input });
    const interpretedCmds = await CmdInterpreter.interpretCommand(commandArr);
    EventBroker.publish('commandInput', { interpretedCmds });
}

/**
 * Return the letter label for a compound (A, B, C, ..., Z, AA, AB, ...).
 * @param {import('BMapsModel').Compound} compound
 * @returns {string | null}
 */
export function getAliasForCompound(compound) {
    const loadedCompounds = App.Workspace.getLoadedCompounds();
    const zeroIndexed = loadedCompounds.indexOf(compound);
    return zeroIndexed !== -1 ? getChainForIndex(zeroIndexed, COMPOUND_LABEL_CHARS) : null;
}

/**
 * Return the compound for a given label (e.g. "A", "B", "AA").
 * @param {string} label
 * @returns {import('BMapsModel').Compound | null}
 */
export function getCompoundForAlias(label) {
    const loadedCompounds = App.Workspace.getLoadedCompounds();
    const zeroIndexed = getIndexForChain(String(label), COMPOUND_LABEL_CHARS);
    if (zeroIndexed === -1 || zeroIndexed >= loadedCompounds.length) {
        console.warn(`Warning: invalid compound label: ${label}. Must be a label like A, B, ..., Z, AA, ...`);
        return null;
    }
    return loadedCompounds[zeroIndexed] || null;
}

/**
 * Convert an entity reference into displayable version.
 * This returns resSpec and select strings as they are.
 * For cmpdIndex, it returns: Compound <label> (<cmpd name>)
 * @param {EntityRef | EntityRef[]} entityRef
 * @returns {string}
 */
export function formatLabels(entityRef) {
    if (Array.isArray(entityRef)) {
        return entityRef.map(formatLabels).join(', ');
    }
    if (!entityRef || typeof entityRef !== 'object') return '(unknown)';
    const { cmpdIndex, resSpec, selectString } = entityRef;
    if (cmpdIndex != null) {
        const cmpd = getCompoundForAlias(cmpdIndex);
        const name = cmpd?.displayName || 'unknown';
        return `Compound ${cmpdIndex} (${name})`;
    }
    if (selectString) return selectString;
    if (resSpec) return resSpec;
    return '(unknown)';
}
